/**
 * RelationFieldType — F12.C T12.C.1 + część B2 mechaniki referencji (Kreator modułów).
 *
 * Pole relacji z pełnym RelationPicker (dropdown + wyszukiwarka).
 * Dwa źródła celu relacji:
 *  - `field.relationSystemModuleSlug` — KARTOTEKA (encja systemowa: pracownicy/kontrahenci/
 *    użytkownicy). Picker pyta dedykowany endpoint kartoteki (część B2). Priorytet nad modułem.
 *  - `field.relationModuleId` — moduł custom (`/modules/:id/records`). Konfigurowane w builderze.
 *
 * Etap 3 (odwołania do kartotek Kreatora): cel = encja `new_table` INNEGO szablonu Kreatora.
 * U klienta taka kartoteka to ZWYKŁY moduł custom (`<srcTemplate>_<entity>`), więc instalacja
 * ustawia po prostu `relationModuleId` — picker renderuje się tą samą ścieżką co relacja
 * custom↔custom (BEZ adaptera systemowego). Gdy szablon-źródło nie jest zainstalowany,
 * instalacja zostawia `relationModuleId` NULL → pole pokazuje „niedostępne" (NotConfigured),
 * nie 500. Patrz „⚠️ OBSZAR ALEXA" niżej (czytelniejszy komunikat + self-heal po instalacji
 * źródła wymagają trwałego znacznika celu po stronie tenanta — Etap 3 W4, do dokończenia).
 *
 * T3 company scoping: `RelationPicker` ma gotowy `companyId` (patrz jego nagłówek), ale
 * ten input NIE go przekazuje — formularze modułów custom są dziś tenant-globalne (brak
 * kontekstu firmy do podania), więc `RelationInput` czeka na przyszłe company-scoped konteksty.
 */

import { Link } from 'lucide-react';

import { OpenRecordCardButton } from '@/components/ui/OpenRecordCardButton';
import { RelationPicker } from '@/components/ui/RelationPicker';
import { RelationTablePickerButton } from '@/components/ui/RelationTablePickerButton';
import { getSystemEntityAdapter } from '@/lib/system-entity-adapters';

import type { FieldInputProps, FieldTypeDefinition } from '../FieldRegistry';

import { RelationCellLabel } from './RelationCellLabel';

function NotConfigured({ slug, message }: { slug: string; message: string }) {
  return (
    <div
      className="rounded-md border border-border-primary bg-bg-secondary px-3 py-2 text-sm text-text-muted"
      data-testid={`field-${slug}-input`}
    >
      <div className="flex items-center gap-1.5">
        <Link className="h-4 w-4 flex-shrink-0 text-warning-500" />
        <span>{message}</span>
      </div>
    </div>
  );
}

function RelationInput({ field, value, onChange, error, disabled }: FieldInputProps) {
  const systemSlug = field.relationSystemModuleSlug ?? null;
  const adapter = getSystemEntityAdapter(systemSlug);

  // Kartoteka wskazana, ale nieobsługiwana w runtime (slug spoza mapy adapterów).
  if (systemSlug && !adapter) {
    return (
      <NotConfigured
        slug={field.slug}
        message={`Kartoteka „${systemSlug}" nie jest jeszcze obsługiwana w wyborze rekordu`}
      />
    );
  }

  // Ani kartoteka, ani moduł custom — pole nie ma skonfigurowanego celu.
  if (!adapter && !field.relationModuleId) {
    return (
      <NotConfigured
        slug={field.slug}
        message="Skonfiguruj moduł docelowy w ustawieniach pola (Kreator formularza)"
      />
    );
  }

  const pickerValue = field.relationMultiple
    ? Array.isArray(value)
      ? value
      : value != null
        ? [String(value)]
        : null
    : typeof value === 'string'
      ? value
      : null;

  return (
    <div data-testid={`field-${field.slug}-input`}>
      <div className="flex items-center gap-2">
        <div className="min-w-0 flex-1">
          <RelationPicker
            moduleId={field.relationModuleId ?? undefined}
            systemModuleSlug={systemSlug}
            value={pickerValue}
            onChange={onChange as (v: string | string[] | null) => void}
            multiple={field.relationMultiple ?? false}
            displayField={field.relationDisplayField ?? 'record_name'}
            placeholder={`Wybierz ${field.name.toLowerCase()}…`}
            disabled={disabled}
            data-testid={`field-${field.slug}-picker`}
          />
        </div>
        {/* Szybkie przejście do karty kartoteki (pojedynczy wybór; działa też w podglądzie). */}
        {!field.relationMultiple && typeof pickerValue === 'string' && (
          <OpenRecordCardButton
            systemSlug={systemSlug}
            recordId={pickerValue}
            testId={`field-${field.slug}-open-card`}
          />
        )}
        {/* Wybór z tabeli (+ otwórz/edytuj/dodaj) — wspólny silnik relacji do kartotek, ten sam
            co „Dostawca" na fakturze. Relacja do modułu własnego nie ma jeszcze tabeli wyboru. */}
        {adapter &&
          (field.relationMultiple ? (
            <RelationTablePickerButton
              entity={adapter.slug}
              multiple
              value={Array.isArray(pickerValue) ? pickerValue : null}
              onChange={(ids) => onChange(ids)}
              disabled={disabled}
              data-testid={`field-${field.slug}-table-picker`}
            />
          ) : (
            <RelationTablePickerButton
              entity={adapter.slug}
              value={typeof pickerValue === 'string' ? pickerValue : null}
              onChange={(id) => onChange(id)}
              disabled={disabled}
              data-testid={`field-${field.slug}-table-picker`}
            />
          ))}
      </div>
      {error && <p className="mt-1 text-xs text-danger-600">{error}</p>}
    </div>
  );
}

export const RelationFieldType: FieldTypeDefinition = {
  type: 'relation',
  renderInput: (props) => <RelationInput {...props} />,
  renderCell: (field, value) => {
    if (!value) return '—';
    // Kartoteka: dociągnij czytelną etykietę (pracownik/kontrahent/użytkownik) zamiast UUID.
    const adapter = getSystemEntityAdapter(field.relationSystemModuleSlug);
    if (adapter) return <RelationCellLabel adapter={adapter} value={value} />;
    // Relacja do modułu custom / brak celu — dotychczasowy render (osobny zakres).
    if (Array.isArray(value)) return `${value.length} rekord${value.length === 1 ? '' : 'ów'}`;
    return typeof value === 'string' ? value.slice(0, 8) + '…' : '—';
  },
  defaultValue: null,
};
